import type { Objective, RunStats, Stage, StarCond } from "./types";

/** Current progress toward a single objective (for HUD display). */
export interface ObjectiveProgress {
  objective: Objective;
  current: number;
  target: number;
  done: boolean;
}

function currentFor(o: Objective, s: RunStats): number {
  switch (o.type) {
    case "reach_tier":
      return s.maxTier;
    case "score":
      return s.score;
    case "merge_count":
      return s.merges;
    case "max_chain":
      return s.maxChain;
    case "clear_blocks":
      return s.blocksCleared;
    case "survive_drops":
      return s.drops;
    case "break_boss":
      return s.bossesBroken;
    case "create_count":
      return s.tierCreated[o.tier ?? 0] ?? 0;
  }
}

export function objectiveProgress(o: Objective, s: RunStats): ObjectiveProgress {
  const current = currentFor(o, s);
  return { objective: o, current, target: o.value, done: current >= o.value };
}

export function allObjectives(stage: Stage, s: RunStats): ObjectiveProgress[] {
  return stage.objectives.map((o) => objectiveProgress(o, s));
}

/** True when every objective is satisfied. */
export function isCleared(stage: Stage, s: RunStats): boolean {
  return stage.objectives.every((o) => objectiveProgress(o, s).done);
}

/** Evaluate one star condition against final run stats (stage already cleared). */
export function evalStar(cond: StarCond, s: RunStats, cleared: boolean): boolean {
  switch (cond.type) {
    case "clear":
      return cleared;
    case "drops_under":
      return cleared && s.drops <= (cond.value ?? Infinity);
    case "max_chain":
      return cleared && s.maxChain >= (cond.value ?? 0);
    case "score_over":
      return cleared && s.score >= (cond.value ?? 0);
    case "no_ability":
      return cleared && s.abilitiesUsed === 0;
  }
}

/** Count stars earned (0..3) for a finished run. */
export function evalStars(stage: Stage, s: RunStats, cleared: boolean): number {
  let stars = 0;
  for (const key of ["1", "2", "3"]) {
    const cond = stage.stars[key];
    if (cond && evalStar(cond, s, cleared)) stars++;
  }
  return stars;
}

export function emptyStats(): RunStats {
  return {
    score: 0,
    drops: 0,
    merges: 0,
    maxChain: 0,
    maxTier: 1,
    tierCreated: {},
    blocksCleared: 0,
    bossesBroken: 0,
    abilitiesUsed: 0,
    elapsedMs: 0,
  };
}
